import React, { useMemo } from "react";
import { Badge } from "../../badge";
import { Button } from "../../button";
import { Checkbox } from "../../checkbox";
import { cn } from "../../utils";
import { hashIntent } from "../../tokens";
import { DataTable, type DataTableColumn } from "../data-table";
import { formatRelativeTime } from "./format";

export interface LeadTableRow {
  id: number;
  /** Synced object id — lets the row open the scope-free /o/<uid> object page. */
  uid?: string;
  display_name: string;
  status_id: number | null;
  source_id: number | null;
  owner_actor_id: number | null;
  last_activity_at: string | null;
  created_at: string;
}

export interface LeadTableProps {
  leads: LeadTableRow[];
  statusLabels?: Record<number, string>;
  sourceLabels?: Record<number, string>;
  ownerLabels?: Record<number, string>;
  selected?: number[];
  onSelectedChange?: (ids: number[]) => void;
  onRowClick?: (id: number) => void;
  bulkActionLabel?: string;
  onBulkAction?: (ids: number[]) => void;
  empty?: React.ReactNode;
  /**
   * Optional filter applied to display_name. The page is the owner of
   * the search input — see CLAUDE.md "Page search is `<SearchInput>`
   * inside `<PageHeader toolbar>`." LeadTable just filters by it.
   */
  query?: string;
}

export function LeadTable({
  leads,
  statusLabels = {},
  sourceLabels = {},
  ownerLabels = {},
  selected,
  onSelectedChange,
  onRowClick,
  bulkActionLabel,
  onBulkAction,
  empty,
  query,
}: LeadTableProps) {
  const filtered = useMemo(() => {
    if (!query) return leads;
    const q = query.toLowerCase();
    return leads.filter((l) => l.display_name.toLowerCase().includes(q));
  }, [leads, query]);
  const selectedSet = new Set(selected ?? []);
  const allSelected = filtered.length > 0 && filtered.every((l) => selectedSet.has(l.id));

  function toggleOne(id: number) {
    if (!onSelectedChange) return;
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedChange([...next]);
  }
  function toggleAll() {
    if (!onSelectedChange) return;
    onSelectedChange(allSelected ? [] : filtered.map((l) => l.id));
  }

  const columns: DataTableColumn<LeadTableRow>[] = [];
  if (onSelectedChange) {
    columns.push({
      key: "select",
      width: "w-10",
      header: (
        <Checkbox
          aria-label="Select all"
          checked={allSelected}
          onCheckedChange={toggleAll}
        />
      ),
      render: (l) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            aria-label={`Select ${l.display_name}`}
            checked={selectedSet.has(l.id)}
            onCheckedChange={() => toggleOne(l.id)}
          />
        </div>
      ),
    });
  }
  columns.push(
    {
      key: "name",
      header: "Name",
      cellClassName: "text-foreground",
      render: (l) => l.display_name,
    },
    {
      key: "status",
      header: "Status",
      width: "w-32",
      align: "center",
      cellClassName: "text-muted-foreground",
      render: (l) => {
        if (l.status_id == null) return "";
        const label = statusLabels[l.status_id];
        if (!label) return "";
        return <Badge variant={hashIntent(label.toLowerCase())}>{label}</Badge>;
      },
    },
    {
      key: "source",
      header: "Source",
      width: "w-32",
      cellClassName: "text-muted-foreground",
      render: (l) =>
        l.source_id != null ? sourceLabels[l.source_id] ?? "" : "",
    },
    {
      key: "owner",
      header: "Owner",
      width: "w-40",
      cellClassName: "text-muted-foreground",
      render: (l) =>
        l.owner_actor_id != null
          ? ownerLabels[l.owner_actor_id] ?? `actor:${l.owner_actor_id}`
          : "",
    },
    {
      key: "last_activity",
      header: "Last activity",
      width: "w-32",
      cellClassName: "text-muted-foreground",
      render: (l) => formatRelativeTime(l.last_activity_at ?? l.created_at),
    },
  );

  return (
    <div className="flex flex-col gap-3">
      {bulkActionLabel && onBulkAction && (selected?.length ?? 0) > 0 && (
        <div className="flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onBulkAction(selected!)}
          >
            {bulkActionLabel} ({selected!.length})
          </Button>
        </div>
      )}
      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(l) => l.id}
        rowClassName={(l) =>
          cn(
            "hover:bg-secondary",
            onRowClick && "cursor-pointer",
            selectedSet.has(l.id) && "bg-muted/40",
          )
        }
        onRowClick={onRowClick ? (l) => onRowClick(l.id) : undefined}
        empty={empty ?? "No leads."}
        tableClassName="table-fixed"
      />
    </div>
  );
}
